import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, Minus, Award, Users, Target } from "lucide-react";

interface ATSCompetitorComparisonProps {
  userScore: number;
  industry: string;
  jobLevel?: string;
}

// Benchmark data by industry and job level
const INDUSTRY_BENCHMARKS: Record<string, { avg: number; top10: number }> = {
  tech: { avg: 68, top10: 88 },
  healthcare: { avg: 65, top10: 85 },
  finance: { avg: 72, top10: 90 },
  marketing: { avg: 66, top10: 86 },
  sales: { avg: 64, top10: 84 },
  education: { avg: 63, top10: 83 },
  legal: { avg: 74, top10: 92 },
  manufacturing: { avg: 62, top10: 82 },
  retail: { avg: 60, top10: 80 },
  hospitality: { avg: 58, top10: 78 },
};

const JOB_LEVEL_BENCHMARKS: Record<string, { avg: number; top10: number }> = {
  entry: { avg: 62, top10: 82 },
  mid: { avg: 68, top10: 86 },
  senior: { avg: 74, top10: 90 },
  executive: { avg: 78, top10: 94 },
};

const INDUSTRY_LABELS: Record<string, string> = {
  tech: "Tech/Software Engineering",
  healthcare: "Healthcare",
  finance: "Finance/Banking",
  marketing: "Marketing/Creative",
  sales: "Sales",
  education: "Education",
  legal: "Legal",
  manufacturing: "Manufacturing",
  retail: "Retail",
  hospitality: "Hospitality",
};

const JOB_LEVEL_LABELS: Record<string, string> = {
  entry: "Entry Level",
  mid: "Mid Level",
  senior: "Senior Level",
  executive: "Executive",
};

export const ATSCompetitorComparison = ({ 
  userScore, 
  industry, 
  jobLevel = "mid" 
}: ATSCompetitorComparisonProps) => {
  const industryBenchmark = INDUSTRY_BENCHMARKS[industry] || INDUSTRY_BENCHMARKS.tech;
  const levelBenchmark = JOB_LEVEL_BENCHMARKS[jobLevel] || JOB_LEVEL_BENCHMARKS.mid;

  // Calculate percentile (simplified - assumes normal distribution)
  const calculatePercentile = (score: number, avg: number, top10: number) => {
    if (score >= top10) return 90 + ((score - top10) / (100 - top10)) * 10;
    if (score >= avg) return 50 + ((score - avg) / (top10 - avg)) * 40;
    return (score / avg) * 50;
  };

  const industryPercentile = Math.round(calculatePercentile(userScore, industryBenchmark.avg, industryBenchmark.top10));
  const levelPercentile = Math.round(calculatePercentile(userScore, levelBenchmark.avg, levelBenchmark.top10));

  const getComparisonIcon = (userScore: number, benchmark: number) => {
    if (userScore > benchmark) return <TrendingUp className="h-5 w-5 text-green-600" />;
    if (userScore < benchmark) return <TrendingDown className="h-5 w-5 text-red-600" />;
    return <Minus className="h-5 w-5 text-yellow-600" />;
  };

  const getComparisonText = (userScore: number, benchmark: number) => {
    const diff = Math.abs(userScore - benchmark);
    if (userScore > benchmark) return `${diff} points above average`;
    if (userScore < benchmark) return `${diff} points below average`;
    return "At average";
  };

  const getPercentileColor = (percentile: number) => {
    if (percentile >= 90) return "text-green-600";
    if (percentile >= 75) return "text-blue-600";
    if (percentile >= 50) return "text-yellow-600";
    return "text-red-600";
  };

  const getPercentileBadge = (percentile: number) => {
    if (percentile >= 90) return { variant: "default" as const, label: "Top 10%" };
    if (percentile >= 75) return { variant: "secondary" as const, label: "Top 25%" };
    if (percentile >= 50) return { variant: "outline" as const, label: "Above Average" };
    return { variant: "destructive" as const, label: "Below Average" };
  };

  return (
    <div className="space-y-6">
      {/* Percentile Overview */}
      <Card className="p-6 bg-gradient-to-br from-primary/5 to-accent/5">
        <div className="flex items-center gap-3 mb-4">
          <Award className="h-6 w-6 text-primary" />
          <h3 className="text-xl font-semibold">Your Competitive Position</h3>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-muted-foreground">
                vs. {INDUSTRY_LABELS[industry] || "Industry"}
              </span>
              <Badge {...getPercentileBadge(industryPercentile)}>
                {getPercentileBadge(industryPercentile).label}
              </Badge>
            </div>
            <p className={`text-4xl font-bold ${getPercentileColor(industryPercentile)}`}>
              {industryPercentile}th
            </p>
            <p className="text-sm text-muted-foreground">
              Your resume scores better than <span className="font-semibold">{industryPercentile}%</span> of resumes in {INDUSTRY_LABELS[industry] || "this industry"}
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-muted-foreground">
                vs. {JOB_LEVEL_LABELS[jobLevel] || "Job Level"}
              </span>
              <Badge {...getPercentileBadge(levelPercentile)}>
                {getPercentileBadge(levelPercentile).label}
              </Badge>
            </div>
            <p className={`text-4xl font-bold ${getPercentileColor(levelPercentile)}`}>
              {levelPercentile}th
            </p>
            <p className="text-sm text-muted-foreground">
              Better than <span className="font-semibold">{levelPercentile}%</span> of {JOB_LEVEL_LABELS[jobLevel] || "job level"} candidates
            </p>
          </div>
        </div>
      </Card>

      {/* Detailed Comparisons */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Industry Comparison */}
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <Users className="h-5 w-5 text-primary" />
            <h4 className="font-semibold">Industry Average</h4>
          </div>
          
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Average Score</span>
                <div className="flex items-center gap-2">
                  {getComparisonIcon(userScore, industryBenchmark.avg)}
                  <span className="font-semibold">{industryBenchmark.avg}</span>
                </div>
              </div>
              <Progress value={industryBenchmark.avg} className="h-2" />
              <p className="text-xs text-muted-foreground mt-1">
                {getComparisonText(userScore, industryBenchmark.avg)}
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Top 10% Threshold</span>
                <div className="flex items-center gap-2">
                  {getComparisonIcon(userScore, industryBenchmark.top10)}
                  <span className="font-semibold">{industryBenchmark.top10}</span>
                </div>
              </div>
              <Progress value={industryBenchmark.top10} className="h-2" />
              <p className="text-xs text-muted-foreground mt-1">
                {userScore >= industryBenchmark.top10 
                  ? "You're in the top 10%!" 
                  : `${industryBenchmark.top10 - userScore} points to top 10%`}
              </p>
            </div>
          </div>
        </Card>

        {/* Job Level Comparison */}
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <Target className="h-5 w-5 text-primary" />
            <h4 className="font-semibold">Job Level Average</h4>
          </div>
          
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Average Score</span>
                <div className="flex items-center gap-2">
                  {getComparisonIcon(userScore, levelBenchmark.avg)}
                  <span className="font-semibold">{levelBenchmark.avg}</span>
                </div>
              </div>
              <Progress value={levelBenchmark.avg} className="h-2" />
              <p className="text-xs text-muted-foreground mt-1">
                {getComparisonText(userScore, levelBenchmark.avg)}
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Top 10% Threshold</span>
                <div className="flex items-center gap-2">
                  {getComparisonIcon(userScore, levelBenchmark.top10)}
                  <span className="font-semibold">{levelBenchmark.top10}</span>
                </div>
              </div>
              <Progress value={levelBenchmark.top10} className="h-2" />
              <p className="text-xs text-muted-foreground mt-1">
                {userScore >= levelBenchmark.top10 
                  ? "You're in the top 10%!" 
                  : `${levelBenchmark.top10 - userScore} points to top 10%`}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Your Score */}
      <Card className="p-5 border-2 border-primary/20">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground mb-1">Your Current Score</p>
            <p className="text-3xl font-bold text-primary">{userScore}</p>
          </div>
          <div className="text-right">
            {userScore >= Math.max(industryBenchmark.top10, levelBenchmark.top10) ? (
              <Badge className="bg-green-600">Excellent Position</Badge>
            ) : userScore >= Math.max(industryBenchmark.avg, levelBenchmark.avg) ? (
              <Badge variant="secondary">Above Average</Badge>
            ) : (
              <Badge variant="outline">Room for Improvement</Badge>
            )}
          </div>
        </div>
        <Progress value={userScore} className="h-2 mt-3" />
      </Card>
    </div>
  );
};
